import React, { useState, useEffect } from 'react';
import { Shield, UserPlus, Edit2, Trash2, Key, Loader2, Users, FileText, Mail, Car, Settings } from 'lucide-react';
import ConfirmModal from '../components/ConfirmModal';
import CustomerTrash from '../components/CustomerTrash';

const AdminPanel = () => {
  const [activeTab, setActiveTab] = useState('users');
  const [currentUser, setCurrentUser] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showUserModal, setShowUserModal] = useState(false);
  const [auditLogs, setAuditLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [userForm, setUserForm] = useState({ username: '', password: '', full_name: '', role: 'user' });
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [message, setMessage] = useState(null);

  // Konfigurace tab
  const [configSubTab, setConfigSubTab] = useState('technologie');
  const [features, setFeatures] = useState([]);
  const [featuresLoading, setFeaturesLoading] = useState(false);
  const [newFeatureName, setNewFeatureName] = useState('');
  const [addingFeature, setAddingFeature] = useState(false);
  const [confirmDeleteFeature, setConfirmDeleteFeature] = useState(null);
  const [matrix, setMatrix] = useState({ features: [], machines: [] });
  const [matrixLoading, setMatrixLoading] = useState(false);
  const [matrixEditing, setMatrixEditing] = useState(false);
  const [matrixChanges, setMatrixChanges] = useState({});
  const [configMessage, setConfigMessage] = useState(null);

  // Email settings state
  const [emailSettings, setEmailSettings] = useState({
    smtp_host: '',
    smtp_port: 587,
    smtp_username: '',
    smtp_password: '',
    from_email: '',
    from_name: '',
    use_tls: true,
    enabled: false
  });
  const [emailLoading, setEmailLoading] = useState(false);

  const fetchAuditLogs = async () => {
    setLogsLoading(true);
    try {
      const res = await fetch("/api/admin/audit-logs?limit=50", { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setAuditLogs(data.data || []);
      }
    } catch (err) {
      console.error(err);
    }
    setLogsLoading(false);
  };

  const fetchEmailSettings = async () => {
    setEmailLoading(true);
    try {
      const res = await fetch('/api/admin/email-settings', { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setEmailSettings(data.data || {});
      }
    } catch (err) {
      console.error(err);
    }
    setEmailLoading(false);
  };

  const fetchFeatures = async () => {
    setFeaturesLoading(true);
    try {
      const res = await fetch('/api/admin/features', { credentials: 'include' });
      if (!res.ok) {
        setConfigMessage({ type: 'error', text: 'Chyba při načítání technologií' });
        setFeaturesLoading(false);
        return;
      }
      const data = await res.json();
      if (data.success) setFeatures(data.data);
    } catch (err) { console.error(err); }
    setFeaturesLoading(false);
  };

  const addFeature = async () => {
    if (!newFeatureName.trim()) return;
    setAddingFeature(true);
    try {
      const res = await fetch('/api/admin/features', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: newFeatureName.trim() })
      });
      const data = await res.json();
      if (data.success) {
        setNewFeatureName('');
        await fetchFeatures();
        setConfigMessage({ type: 'success', text: 'Technologie přidána' });
      } else {
        setConfigMessage({ type: 'error', text: data.error || 'Chyba' });
      }
    } catch (err) {
      setConfigMessage({ type: 'error', text: 'Chyba při přidávání' });
    }
    setAddingFeature(false);
    setTimeout(() => setConfigMessage(null), 3000);
  };

  const deleteFeature = async (id) => {
    try {
      const res = await fetch(`/api/admin/features/${id}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      const data = await res.json();
      if (data.success) {
        await fetchFeatures();
        if (matrixEditing) await fetchMatrix();
        setConfigMessage({ type: 'success', text: 'Technologie smazána' });
      } else {
        setConfigMessage({ type: 'error', text: data.error || 'Chyba' });
      }
    } catch (err) {
      setConfigMessage({ type: 'error', text: 'Chyba při mazání' });
    }
    // Clear any pending matrix changes for the deleted feature
    setMatrixChanges(prev => {
      const next = { ...prev };
      Object.keys(next).forEach(key => {
        if (key.endsWith(`_${id}`)) delete next[key];
      });
      return next;
    });
    setConfirmDeleteFeature(null);
    setTimeout(() => setConfigMessage(null), 3000);
  };

  const fetchMatrix = async () => {
    setMatrixLoading(true);
    try {
      const res = await fetch('/api/admin/features/matrix', { credentials: 'include' });
      if (!res.ok) {
        setConfigMessage({ type: 'error', text: 'Chyba při načítání matice' });
        setMatrixLoading(false);
        return;
      }
      const data = await res.json();
      if (data.success) setMatrix(data.data);
    } catch (err) { console.error(err); }
    setMatrixLoading(false);
  };

  const saveMatrix = async () => {
    const updates = Object.entries(matrixChanges).map(([key, has_feature]) => {
      const [machine_id, feature_id] = key.split('_').map(Number);
      return { machine_id, feature_id, has_feature };
    });
    if (updates.length === 0) return;
    try {
      const res = await fetch('/api/admin/features/matrix', {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates })
      });
      const data = await res.json();
      if (data.success) {
        setMatrixEditing(false);
        setMatrixChanges({});
        await fetchMatrix();
        setConfigMessage({ type: 'success', text: 'Konfigurace uložena' });
      } else {
        setConfigMessage({ type: 'error', text: data.error || 'Chyba' });
      }
    } catch (err) {
      setConfigMessage({ type: 'error', text: 'Chyba při ukládání' });
    }
    setTimeout(() => setConfigMessage(null), 3000);
  };

  useEffect(() => {
    fetchCurrentUser();
    if (activeTab === 'users') fetchUsers();
    else if (activeTab === 'logs') fetchAuditLogs();
    else if (activeTab === 'email') fetchEmailSettings();
    else if (activeTab === 'konfigurace') {
      fetchFeatures();
      if (configSubTab === 'seznam') fetchMatrix();
    }
  }, [activeTab, configSubTab]);

  const fetchCurrentUser = async () => {
    try {
      const res = await fetch('/api/auth/me', { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setCurrentUser(data.user);
      }
    } catch (err) {
      console.error('Failed to fetch current user:', err);
    }
  };

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const res = await fetch('/api/admin/users', { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setUsers(data.data || []);
      }
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  };

  const handleGeneratePassword = async () => {
    try {
      const res = await fetch('/api/admin/users/generate-password', {
        method: 'POST',
        credentials: 'include'
      });
      if (res.ok) {
        const data = await res.json();
        setUserForm({ ...userForm, password: data.password });
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleSaveUser = async (e) => {
    e.preventDefault();
    try {
      const url = editingUser ? `/api/admin/users/${editingUser.id}` : '/api/admin/users';
      const method = editingUser ? 'PUT' : 'POST';
      
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(userForm)
      });

      const data = await res.json();
      if (res.ok) {
        setMessage({ type: 'success', text: data.message });
        setShowUserModal(false);
        setEditingUser(null);
        setUserForm({ username: '', password: '', full_name: '', role: 'user' });
    fetchCurrentUser();
        fetchUsers();
      } else {
        setMessage({ type: 'error', text: data.error || 'Chyba při ukládání' });
      }
    } catch (err) {
      setMessage({ type: 'error', text: 'Chyba spojení' });
    }
  };

  const handleDeleteUser = async (user) => {
    try {
      const res = await fetch(`/api/admin/users/${user.id}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      const data = await res.json();
      if (res.ok) {
        setMessage({ type: 'success', text: 'Uživatel smazán' });
    fetchCurrentUser();
        fetchUsers();
      } else {
        setMessage({ type: 'error', text: data.error });
      }
    } catch (err) {
      setMessage({ type: 'error', text: 'Chyba spojení' });
    }
    setConfirmDelete(null);
  };

  const handleSaveEmailSettings = async (e) => {
    e.preventDefault();
    setEmailLoading(true);
    try {
      const res = await fetch('/api/admin/email-settings', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(emailSettings)
      });

      const data = await res.json();
      if (res.ok) {
        setMessage({ type: 'success', text: data.message || 'Email nastavení uloženo' });
        fetchEmailSettings(); // Refresh to get updated data
      } else {
        setMessage({ type: 'error', text: data.error || 'Chyba při ukládání' });
      }
    } catch (err) {
      setMessage({ type: 'error', text: 'Chyba spojení' });
    }
    setEmailLoading(false);
  };

  const openEditModal = (user) => {
    setEditingUser(user);
    setUserForm({
      username: user.username,
      password: '',
      full_name: user.full_name || '',
      role: user.role
    });
    setShowUserModal(true);
  };

  const openNewModal = () => {
    setEditingUser(null);
    setUserForm({ username: '', password: '', full_name: '', role: 'user' });
    setShowUserModal(true);
  };

  const roleLabels = {
    admin: 'Administrátor',
    manager: 'Manažer',
    user: 'Uživatel'
  };

  const roleColors = {
    admin: { bg: '#FFEBEE', color: '#E53935' },
    manager: { bg: '#E3F2FD', color: '#1976D2' },
    user: { bg: '#F5F5F5', color: '#666' }
  };

  return (
    <div style={{ padding: '24px' }}>
      <h1 style={{ fontSize: '24px', fontWeight: 700, color: '#333', margin: '0 0 24px', display: 'flex', alignItems: 'center', gap: '8px' }}>
        <Shield color="#E53935" /> Administrace
      </h1>

      <div style={{ backgroundColor: '#FFF', borderRadius: '12px', border: '1px solid #E0E0E0', padding: '24px' }}>
        <div style={{ display: 'flex', gap: '8px', borderBottom: '1px solid #E0E0E0', marginBottom: '24px', paddingBottom: '12px' }}>
          <button
            onClick={() => setActiveTab('users')}
            style={{
              padding: '10px 20px',
              borderRadius: '8px',
              border: 'none',
              backgroundColor: activeTab === 'users' ? '#E53935' : '#F5F5F5',
              color: activeTab === 'users' ? '#FFF' : '#666',
              cursor: 'pointer',
              fontWeight: 500,
              fontSize: '14px',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}
          >
            <Users size={16} /> Uživatelé
          </button>
          <button
            onClick={() => setActiveTab("logs")}
            style={{
              padding: "10px 20px",
              borderRadius: "8px",
              border: "none",
              backgroundColor: activeTab === "logs" ? "#E53935" : "#F5F5F5",
              color: activeTab === "logs" ? "#FFF" : "#666",
              cursor: "pointer",
              fontWeight: 500,
              fontSize: "14px",
              display: "flex",
              alignItems: "center",
              gap: "6px"
            }}
          >
            <FileText size={16} /> Logy
          </button>
          <button
            onClick={() => setActiveTab("email")}
            style={{
              padding: "10px 20px",
              borderRadius: "8px",
              border: "none",
              backgroundColor: activeTab === "email" ? "#E53935" : "#F5F5F5",
              color: activeTab === "email" ? "#FFF" : "#666",
              cursor: "pointer",
              fontWeight: 500,
              fontSize: "14px",
              display: "flex",
              alignItems: "center",
              gap: "6px"
            }}
          >
            <Mail size={16} /> Email
          </button>
          <button
            onClick={() => setActiveTab("docs")}
            style={{
              padding: "10px 20px",
              borderRadius: "8px",
              border: "none",
              backgroundColor: activeTab === "docs" ? "#E53935" : "#F5F5F5",
              color: activeTab === "docs" ? "#FFF" : "#666",
              cursor: "pointer",
              fontWeight: 500,
              fontSize: "14px",
              display: "flex",
              alignItems: "center",
              gap: "6px"
            }}
          >
            <FileText size={16} /> Dokumentace
          </button>
          <button
            onClick={() => setActiveTab('konfigurace')}
            style={{
              padding: '10px 20px',
              borderRadius: '8px',
              border: 'none',
              backgroundColor: activeTab === 'konfigurace' ? '#E53935' : '#F5F5F5',
              color: activeTab === 'konfigurace' ? '#FFF' : '#666',
              cursor: 'pointer',
              fontWeight: 500,
              fontSize: '14px',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}
          >
            <Settings size={16} /> Konfigurace
          </button>
          <button
            onClick={() => setActiveTab('kos')}
            style={{
              padding: '10px 20px',
              borderRadius: '8px',
              border: 'none',
              backgroundColor: activeTab === 'kos' ? '#E53935' : '#F5F5F5',
              color: activeTab === 'kos' ? '#FFF' : '#666',
              cursor: 'pointer',
              fontWeight: 500,
              fontSize: '14px',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}
          >
            <Trash2 size={16} /> Koš
          </button>
        </div>

        {activeTab === 'kos' && <CustomerTrash />}

        {message && (
          <div style={{
            padding: '12px 16px',
            borderRadius: '8px',
            marginBottom: '16px',
            backgroundColor: message.type === 'success' ? '#E8F5E9' : '#FFEBEE',
            color: message.type === 'success' ? '#388E3C' : '#E53935',
            fontSize: '14px'
          }}>
            {message.text}
          </div>
        )}

        {activeTab === 'users' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ fontSize: '16px', fontWeight: 600, margin: 0 }}>Správa uživatelů</h3>
              <button
                onClick={openNewModal}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '10px 16px',
                  backgroundColor: '#E53935',
                  color: '#FFF',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: 500
                }}
              >
                <UserPlus size={16} /> Nový uživatel
              </button>
            </div>

            {loading ? (
              <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                <Loader2 size={24} className="spin" />
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {users.map((user) => {
                  const rc = roleColors[user.role] || roleColors.user;
                  return (
                    <div key={user.id} style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '16px',
                      backgroundColor: '#F9F9F9',
                      borderRadius: '10px'
                    }}>
                      <div style={{ flex: 1 }}>
                        <p style={{ fontWeight: 600, color: '#333', margin: '0 0 4px' }}>
                          {user.full_name || user.username}
                          <span style={{
                            marginLeft: '8px',
                            fontSize: '12px',
                            padding: '2px 8px',
                            borderRadius: '4px',
                            backgroundColor: rc.bg,
                            color: rc.color
                          }}>
                            {roleLabels[user.role]}
                          </span>
                        </p>
                        <p style={{ fontSize: '13px', color: '#999', margin: 0 }}>@{user.username}</p>
                      </div>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        {user.role !== 'admin' && (
                          <button
                            onClick={() => openEditModal(user)}
                            title="Upravit uživatele"
                            style={{
                              padding: '8px',
                              borderRadius: '8px',
                              border: 'none',
                              backgroundColor: '#E3F2FD',
                              cursor: 'pointer'
                            }}
                          >
                            <Edit2 size={16} color="#1976D2" />
                          </button>
                        )}
                        {currentUser?.role === 'admin' && user.role !== 'admin' && (
                          <button
                            onClick={() => setConfirmDelete(user)}
                            title="Smazat uživatele"
                            style={{
                              padding: '8px',
                              borderRadius: '8px',
                              border: 'none',
                              backgroundColor: '#FFEBEE',
                              cursor: 'pointer'
                            }}
                          >
                            <Trash2 size={16} color="#E53935" />
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {activeTab === 'logs' && (
          <div>
            <h3 style={{ fontSize: '16px', fontWeight: 600, margin: '0 0 16px' }}>Globální log</h3>
            {logsLoading ? (
              <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                <Loader2 size={24} className="spin" />
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {auditLogs.map((log) => {
                  const actionLabels = { create: 'Vytvořeno', update: 'Upraveno', delete: 'Smazáno', password_reset: 'Reset hesla' };
                  const actionColors = { create: { bg: '#E8F5E9', color: '#388E3C' }, update: { bg: '#E3F2FD', color: '#1976D2' }, delete: { bg: '#FFEBEE', color: '#E53935' } };
                  const ac = actionColors[log.action] || { bg: '#F5F5F5', color: '#666' };
                  return (
                    <div key={log.id} style={{ padding: '16px', backgroundColor: '#F9F9F9', borderRadius: '10px', border: '1px solid #E0E0E0' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                        <div>
                          <span style={{ fontSize: '12px', padding: '2px 8px', borderRadius: '4px', backgroundColor: ac.bg, color: ac.color, fontWeight: 500 }}>
                            {actionLabels[log.action] || log.action}
                          </span>
                          <span style={{ marginLeft: '8px', fontSize: '13px', color: '#666' }}>{log.entity_type}</span>
                        </div>
                        <p style={{ fontSize: '12px', color: '#999', margin: 0 }}>{new Date(log.created_at).toLocaleString('cs-CZ')}</p>
                      </div>
                      <p style={{ fontSize: '14px', fontWeight: 600, color: '#333', margin: 0 }}>{log.username || 'Neznámý'}</p>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {activeTab === 'email' && (
          <div>
            <h3 style={{ fontSize: '16px', fontWeight: 600, margin: '0 0 16px' }}>Email nastavení</h3>
            {emailLoading ? (
              <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                <Loader2 size={24} className="spin" />
              </div>
            ) : (
              <form onSubmit={handleSaveEmailSettings}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: 500, marginBottom: '6px' }}>
                      SMTP Server
                    </label>
                    <input
                      type="text"
                      value={emailSettings.smtp_host || ''}
                      onChange={(e) => setEmailSettings({ ...emailSettings, smtp_host: e.target.value })}
                      placeholder="smtp.gmail.com"
                      style={{ width: '100%', padding: '10px', border: '1px solid #E0E0E0', borderRadius: '8px', fontSize: '14px' }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: 500, marginBottom: '6px' }}>
                      SMTP Port
                    </label>
                    <input
                      type="number"
                      value={emailSettings.smtp_port || 587}
                      onChange={(e) => setEmailSettings({ ...emailSettings, smtp_port: parseInt(e.target.value) || 587 })}
                      style={{ width: '100%', padding: '10px', border: '1px solid #E0E0E0', borderRadius: '8px', fontSize: '14px' }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: 500, marginBottom: '6px' }}>
                      SMTP Username
                    </label>
                    <input
                      type="text"
                      value={emailSettings.smtp_username || ''}
                      onChange={(e) => setEmailSettings({ ...emailSettings, smtp_username: e.target.value })}
                      placeholder="your-email@gmail.com"
                      style={{ width: '100%', padding: '10px', border: '1px solid #E0E0E0', borderRadius: '8px', fontSize: '14px' }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: 500, marginBottom: '6px' }}>
                      SMTP Password
                    </label>
                    <input
                      type="password"
                      value={emailSettings.smtp_password || ''}
                      onChange={(e) => setEmailSettings({ ...emailSettings, smtp_password: e.target.value })}
                      placeholder="Ponechte prázdné pro nezměnit"
                      style={{ width: '100%', padding: '10px', border: '1px solid #E0E0E0', borderRadius: '8px', fontSize: '14px' }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: 500, marginBottom: '6px' }}>
                      Odesílatel (Email)
                    </label>
                    <input
                      type="email"
                      value={emailSettings.from_email || ''}
                      onChange={(e) => setEmailSettings({ ...emailSettings, from_email: e.target.value })}
                      placeholder="noreply@company.com"
                      style={{ width: '100%', padding: '10px', border: '1px solid #E0E0E0', borderRadius: '8px', fontSize: '14px' }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: 500, marginBottom: '6px' }}>
                      Odesílatel (Jméno)
                    </label>
                    <input
                      type="text"
                      value={emailSettings.from_name || ''}
                      onChange={(e) => setEmailSettings({ ...emailSettings, from_name: e.target.value })}
                      placeholder="BASICGAME ERP"
                      style={{ width: '100%', padding: '10px', border: '1px solid #E0E0E0', borderRadius: '8px', fontSize: '14px' }}
                    />
                  </div>
                </div>
                <div style={{ marginTop: '16px', display: 'flex', gap: '16px', alignItems: 'center' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={emailSettings.use_tls || false}
                      onChange={(e) => setEmailSettings({ ...emailSettings, use_tls: e.target.checked })}
                      style={{ width: '16px', height: '16px' }}
                    />
                    <span style={{ fontSize: '14px' }}>Použít TLS</span>
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={emailSettings.enabled || false}
                      onChange={(e) => setEmailSettings({ ...emailSettings, enabled: e.target.checked })}
                      style={{ width: '16px', height: '16px' }}
                    />
                    <span style={{ fontSize: '14px', fontWeight: 600 }}>Povolit odesílání emailů</span>
                  </label>
                </div>
                <div style={{ marginTop: '24px' }}>
                  <button
                    type="submit"
                    disabled={emailLoading}
                    style={{
                      padding: '12px 24px',
                      backgroundColor: '#E53935',
                      color: '#FFF',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: emailLoading ? 'not-allowed' : 'pointer',
                      fontSize: '14px',
                      fontWeight: 500,
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}
                  >
                    {emailLoading && <Loader2 size={16} className="spin" />}
                    Uložit nastavení
                  </button>
                </div>
              </form>
            )}
          </div>
        )}

        {/* Dokumentace šablon */}
        {activeTab === 'docs' && (
          <div style={{ backgroundColor: '#FFF', borderRadius: '12px', border: '1px solid #E0E0E0', padding: '24px', maxWidth: '900px' }}>
            <h2 style={{ fontSize: '20px', fontWeight: 700, color: '#333', marginBottom: '24px' }}>
              <FileText size={24} color="#E53935" style={{ display: 'inline', marginRight: '12px' }} />
              Dokumentace: Tvoření šablon
            </h2>
            
            <div style={{ marginBottom: '24px', lineHeight: '1.6' }}>
              <h3 style={{ fontSize: '18px', fontWeight: 600, color: '#E53935', marginBottom: '12px' }}>📋 Co jsou šablony?</h3>
              <p>Šablony jsou <strong>.docx dokumenty</strong>, které obsahují placeholdery – speciální značky nahrazené skutečnými daty při generování.</p>
            </div>

            <div style={{ marginBottom: '24px' }}>
              <h3 style={{ fontSize: '18px', fontWeight: 600, color: '#E53935', marginBottom: '12px' }}>🏷️ Placeholdery</h3>
              
              <div style={{ backgroundColor: '#F5F5F5', padding: '16px', borderRadius: '8px', marginBottom: '16px' }}>
                <h4 style={{ fontSize: '16px', fontWeight: 600, color: '#1976D2', marginBottom: '12px' }}>Zákazník:</h4>
                <div style={{ display: 'grid', gap: '8px', fontSize: '14px', lineHeight: '1.8' }}>
                  <div><code style={{ backgroundColor: '#FFF', padding: '4px 8px', borderRadius: '4px', fontFamily: 'monospace', color: '#E53935' }}>{'{customer_name}'}</code> – Název autoškoly</div>
                  <div><code style={{ backgroundColor: '#FFF', padding: '4px 8px', borderRadius: '4px', fontFamily: 'monospace', color: '#E53935' }}>{'{customer_ico}'}</code> – IČO</div>
                  <div><code style={{ backgroundColor: '#FFF', padding: '4px 8px', borderRadius: '4px', fontFamily: 'monospace', color: '#E53935' }}>{'{customer_dic}'}</code> – DIČ</div>
                  <div><code style={{ backgroundColor: '#FFF', padding: '4px 8px', borderRadius: '4px', fontFamily: 'monospace', color: '#E53935' }}>{'{customer_street}'}</code> – Ulice a číslo popisné</div>
                  <div><code style={{ backgroundColor: '#FFF', padding: '4px 8px', borderRadius: '4px', fontFamily: 'monospace', color: '#E53935' }}>{'{customer_city}'}</code> – Město</div>
                  <div><code style={{ backgroundColor: '#FFF', padding: '4px 8px', borderRadius: '4px', fontFamily: 'monospace', color: '#E53935' }}>{'{customer_postal_code}'}</code> – PSČ</div>
                  <div><code style={{ backgroundColor: '#FFF', padding: '4px 8px', borderRadius: '4px', fontFamily: 'monospace', color: '#E53935' }}>{'{customer_contact_name}'}</code> – Jméno kontaktní osoby</div>
                  <div><code style={{ backgroundColor: '#FFF', padding: '4px 8px', borderRadius: '4px', fontFamily: 'monospace', color: '#E53935' }}>{'{customer_phone}'}</code> – Telefon</div>
                  <div><code style={{ backgroundColor: '#FFF', padding: '4px 8px', borderRadius: '4px', fontFamily: 'monospace', color: '#E53935' }}>{'{customer_email}'}</code> – Email</div>
                </div>
              </div>

              <div style={{ backgroundColor: '#F5F5F5', padding: '16px', borderRadius: '8px', marginBottom: '16px' }}>
                <h4 style={{ fontSize: '16px', fontWeight: 600, color: '#4CAF50', marginBottom: '12px' }}>Trenažér:</h4>
                <div style={{ display: 'grid', gap: '8px', fontSize: '14px', lineHeight: '1.8' }}>
                  <div><code style={{ backgroundColor: '#FFF', padding: '4px 8px', borderRadius: '4px', fontFamily: 'monospace', color: '#E53935' }}>{'{machine_serial}'}</code> – Výrobní číslo (např. "32026001")</div>
                  <div><code style={{ backgroundColor: '#FFF', padding: '4px 8px', borderRadius: '4px', fontFamily: 'monospace', color: '#E53935' }}>{'{machine_model}'}</code> – Model trenažéru ("Autom TR3" nebo "Autom TR4")</div>
                  <div><code style={{ backgroundColor: '#FFF', padding: '4px 8px', borderRadius: '4px', fontFamily: 'monospace', color: '#E53935' }}>{'{machine_status}'}</code> – Status trenažéru (např. "Nahled", "Objednáno")</div>
                  <div><code style={{ backgroundColor: '#FFF', padding: '4px 8px', borderRadius: '4px', fontFamily: 'monospace', color: '#E53935' }}>{'{contract_date}'}</code> – Datum podpisu smlouvy (formát DD.MM.YYYY)</div>
                  <div><code style={{ backgroundColor: '#FFF', padding: '4px 8px', borderRadius: '4px', fontFamily: 'monospace', color: '#E53935' }}>{'{delivery_date}'}</code> – Datum doručení (formát DD.MM.YYYY)</div>
                  <div><code style={{ backgroundColor: '#FFF', padding: '4px 8px', borderRadius: '4px', fontFamily: 'monospace', color: '#E53935' }}>{'{os_code}'}</code> – OS kód (OS + výrobní číslo, např. "OS32026001")</div>
                  <div><code style={{ backgroundColor: '#FFF', padding: '4px 8px', borderRadius: '4px', fontFamily: 'monospace', color: '#E53935' }}>{'{pp_code}'}</code> – PP kód (PP + výrobní číslo, např. "PP32026001")</div>
                  <div><code style={{ backgroundColor: '#FFF', padding: '4px 8px', borderRadius: '4px', fontFamily: 'monospace', color: '#E53935' }}>{'{ce_code}'}</code> – CE kód (CE + výrobní číslo, např. "CE32026001")</div>
                </div>
              </div>

              <div style={{ backgroundColor: '#F5F5F5', padding: '16px', borderRadius: '8px' }}>
                <h4 style={{ fontSize: '16px', fontWeight: 600, color: '#FF9800', marginBottom: '12px' }}>Systémové:</h4>
                <div style={{ display: 'grid', gap: '8px', fontSize: '14px', lineHeight: '1.8' }}>
                  <div><code style={{ backgroundColor: '#FFF', padding: '4px 8px', borderRadius: '4px', fontFamily: 'monospace', color: '#E53935' }}>{'{current_date}'}</code> – Aktuální datum generování (formát DD.MM.YYYY)</div>
                </div>
              </div>
            </div>

            <div style={{ marginBottom: '24px' }}>
              <h3 style={{ fontSize: '18px', fontWeight: 600, color: '#E53935', marginBottom: '12px' }}>📝 Jak vytvořit šablonu?</h3>
              <ol style={{ paddingLeft: '20px', lineHeight: '1.8' }}>
                <li>Otevři Microsoft Word</li>
                <li>Vytvoř dokument a vlož placeholdery (např. {"{customer_name}"})</li>
                <li>Ulož jako .docx</li>
                <li>Nahraj v sekci Šablony</li>
                <li>Při generování se placeholdery automaticky nahradí</li>
              </ol>
            </div>

            <div style={{ padding: '12px', backgroundColor: '#FFF9C4', border: '1px solid #FBC02D', borderRadius: '8px' }}>
              ⚠️ <strong>Poznámka:</strong> Placeholdery musí být přesně ve formátu {"{nazev}"} (složené závorky).
            </div>
          </div>
        )}

        {activeTab === 'konfigurace' && (
          <div>
            {configMessage && (
              <div style={{
                padding: '12px 16px',
                borderRadius: '8px',
                marginBottom: '16px',
                backgroundColor: configMessage.type === 'success' ? '#E8F5E9' : '#FFEBEE',
                color: configMessage.type === 'success' ? '#388E3C' : '#E53935',
                fontSize: '14px'
              }}>
                {configMessage.text}
              </div>
            )}

            {/* Sub-tab switcher */}
            <div style={{ display: 'flex', gap: '8px', marginBottom: '20px' }}>
              <button
                onClick={() => { setConfigSubTab('technologie'); }}
                style={{
                  padding: '8px 16px', borderRadius: '6px', border: 'none',
                  backgroundColor: configSubTab === 'technologie' ? '#E53935' : '#F5F5F5',
                  color: configSubTab === 'technologie' ? '#FFF' : '#666',
                  cursor: 'pointer', fontWeight: 500, fontSize: '13px'
                }}
              >
                Technologie
              </button>
              <button
                onClick={() => {
                  setConfigSubTab('seznam');
                  fetchMatrix();
                }}
                style={{
                  padding: '8px 16px', borderRadius: '6px', border: 'none',
                  backgroundColor: configSubTab === 'seznam' ? '#E53935' : '#F5F5F5',
                  color: configSubTab === 'seznam' ? '#FFF' : '#666',
                  cursor: 'pointer', fontWeight: 500, fontSize: '13px'
                }}
              >
                Seznam
              </button>
            </div>

            {/* Technologie sub-tab */}
            {configSubTab === 'technologie' && (
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
                  <input
                    type="text"
                    value={newFeatureName}
                    onChange={e => setNewFeatureName(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && addFeature()}
                    placeholder="Název technologie..."
                    style={{
                      padding: '8px 12px', borderRadius: '6px',
                      border: '1px solid #E0E0E0', fontSize: '14px',
                      width: '280px', outline: 'none'
                    }}
                  />
                  <button
                    onClick={addFeature}
                    disabled={addingFeature || !newFeatureName.trim()}
                    style={{
                      padding: '8px 16px', borderRadius: '6px', border: 'none',
                      backgroundColor: newFeatureName.trim() ? '#E53935' : '#CCC',
                      color: '#FFF', cursor: newFeatureName.trim() ? 'pointer' : 'not-allowed',
                      fontWeight: 600, fontSize: '13px'
                    }}
                  >
                    + Přidat
                  </button>
                </div>

                {featuresLoading ? (
                  <div style={{ color: '#999', fontSize: '14px' }}>Načítání...</div>
                ) : features.length === 0 ? (
                  <div style={{ color: '#999', fontSize: '14px' }}>Žádné technologie.</div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    {features.map(f => (
                      <div key={f.id} style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        padding: '10px 14px', borderRadius: '8px',
                        border: '1px solid #E0E0E0', backgroundColor: '#FAFAFA'
                      }}>
                        <span style={{ fontSize: '14px', color: '#333' }}>{f.name}</span>
                        <button
                          onClick={() => setConfirmDeleteFeature(f)}
                          style={{
                            background: 'none', border: 'none', cursor: 'pointer',
                            color: '#E53935', padding: '4px', borderRadius: '4px'
                          }}
                          title="Smazat"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Seznam sub-tab */}
            {configSubTab === 'seznam' && (
              <div>
                <div style={{ display: 'flex', gap: '8px', marginBottom: '12px', alignItems: 'center' }}>
                  {!matrixEditing ? (
                    <button
                      onClick={() => setMatrixEditing(true)}
                      style={{
                        padding: '8px 16px', borderRadius: '6px', border: 'none',
                        backgroundColor: '#E53935', color: '#FFF',
                        cursor: 'pointer', fontWeight: 600, fontSize: '13px'
                      }}
                    >
                      Upravit
                    </button>
                  ) : (
                    <>
                      <button
                        onClick={saveMatrix}
                        disabled={Object.keys(matrixChanges).length === 0}
                        style={{
                          padding: '8px 16px', borderRadius: '6px', border: 'none',
                          backgroundColor: Object.keys(matrixChanges).length > 0 ? '#388E3C' : '#CCC',
                          color: '#FFF',
                          cursor: Object.keys(matrixChanges).length > 0 ? 'pointer' : 'not-allowed',
                          fontWeight: 600, fontSize: '13px'
                        }}
                      >
                        Uložit změny
                      </button>
                      <button
                        onClick={() => { setMatrixEditing(false); setMatrixChanges({}); }}
                        style={{
                          padding: '8px 16px', borderRadius: '6px', border: 'none',
                          backgroundColor: '#F5F5F5', color: '#666',
                          cursor: 'pointer', fontWeight: 500, fontSize: '13px'
                        }}
                      >
                        Zrušit
                      </button>
                    </>
                  )}
                </div>

                {matrixLoading ? (
                  <div style={{ color: '#999', fontSize: '14px' }}>Načítání...</div>
                ) : matrix.features.length === 0 ? (
                  <div style={{ color: '#999', fontSize: '14px' }}>Žádné technologie nejsou definovány.</div>
                ) : matrix.machines.length === 0 ? (
                  <div style={{ color: '#999', fontSize: '14px' }}>Žádné trenažéry.</div>
                ) : (
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ borderCollapse: 'collapse', fontSize: '13px', minWidth: '100%' }}>
                      <thead>
                        <tr>
                          <th style={{
                            textAlign: 'left', padding: '8px 12px',
                            borderBottom: '2px solid #E0E0E0', whiteSpace: 'nowrap',
                            position: 'sticky', left: 0, backgroundColor: '#FFF', zIndex: 1
                          }}>
                            Zákazník
                          </th>
                          {matrix.features.map(f => (
                            <th key={f.id} style={{
                              padding: '8px 12px', borderBottom: '2px solid #E0E0E0',
                              whiteSpace: 'nowrap', textAlign: 'center', color: '#666'
                            }}>
                              {f.name}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {matrix.machines.map(machine => (
                          <tr key={machine.id} style={{ borderBottom: '1px solid #F0F0F0' }}>
                            <td style={{
                              padding: '8px 12px', whiteSpace: 'nowrap',
                              position: 'sticky', left: 0, backgroundColor: '#FFF',
                              fontWeight: 500, color: '#333'
                            }}>
                              <div>{machine.customer_name || '—'}</div>
                              <div style={{ fontSize: '11px', color: '#999' }}>
                                {machine.model_type} · {machine.serial_number}
                              </div>
                            </td>
                            {matrix.features.map(f => {
                              const key = `${machine.id}_${f.id}`;
                              const current = key in matrixChanges
                                ? matrixChanges[key]
                                : (machine.features[String(f.id)] ?? false);
                              return (
                                <td key={f.id} style={{ textAlign: 'center', padding: '8px 12px' }}>
                                  {matrixEditing ? (
                                    <input
                                      type="checkbox"
                                      checked={current}
                                      onChange={() => {
                                        setMatrixChanges(prev => ({ ...prev, [key]: !current }));
                                      }}
                                      style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                                    />
                                  ) : (
                                    <span style={{
                                      fontSize: '16px',
                                      color: current ? '#388E3C' : '#E53935'
                                    }}>
                                      {current ? '✓' : '✗'}
                                    </span>
                                  )}
                                </td>
                              );
                            })}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {/* Delete confirmation modal for features */}
            {confirmDeleteFeature && (
              <ConfirmModal
                show={!!confirmDeleteFeature}
                title="Smazat technologii?"
                message={`Smazat technologii "${confirmDeleteFeature.name}"? Odstraní se ze všech trenažérů.`}
                confirmText="Smazat"
                cancelText="Zrušit"
                danger={true}
                onConfirm={() => deleteFeature(confirmDeleteFeature.id)}
                onCancel={() => setConfirmDeleteFeature(null)}
              />
            )}
          </div>
        )}
      </div>

      {/* User Modal */}
      {showUserModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', backgroundColor: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000 }}>
          <div style={{ backgroundColor: '#FFF', borderRadius: '16px', padding: '24px', width: '100%', maxWidth: '500px', boxShadow: '0 20px 25px rgba(0,0,0,0.15)' }}>
            <h3 style={{ fontSize: '18px', fontWeight: 700, marginBottom: '20px' }}>
              {editingUser ? 'Upravit uživatele' : 'Nový uživatel'}
            </h3>
            <form onSubmit={handleSaveUser}>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 500, marginBottom: '6px' }}>
                  Uživatelské jméno
                </label>
                <input
                  type="text"
                  value={userForm.username}
                  onChange={(e) => setUserForm({ ...userForm, username: e.target.value })}
                  required
                  style={{ width: '100%', padding: '10px', border: '1px solid #E0E0E0', borderRadius: '8px', fontSize: '14px' }}
                />
              </div>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 500, marginBottom: '6px' }}>
                  Celé jméno
                </label>
                <input
                  type="text"
                  value={userForm.full_name}
                  onChange={(e) => setUserForm({ ...userForm, full_name: e.target.value })}
                  style={{ width: '100%', padding: '10px', border: '1px solid #E0E0E0', borderRadius: '8px', fontSize: '14px' }}
                />
              </div>
              <div style={{ marginBottom: '16px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                  <label style={{ fontSize: '13px', fontWeight: 500 }}>
                    Heslo {editingUser && '(nechte prázdné pro nezměnit)'}
                  </label>
                  <button
                    type="button"
                    onClick={handleGeneratePassword}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                      padding: '4px 8px',
                      fontSize: '12px',
                      backgroundColor: '#F5F5F5',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer'
                    }}
                  >
                    <Key size={12} /> Generovat
                  </button>
                </div>
                <input
                  type="text" autoComplete="new-password"
                  value={userForm.password}
                  onChange={(e) => setUserForm({ ...userForm, password: e.target.value })}
                  required={!editingUser}
                  style={{ width: '100%', padding: '10px', border: '1px solid #E0E0E0', borderRadius: '8px', fontSize: '14px' }}
                />
              </div>
              <div style={{ marginBottom: '24px' }}>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 500, marginBottom: '6px' }}>
                  Oprávnění
                </label>
                <select
                  value={userForm.role}
                  onChange={(e) => setUserForm({ ...userForm, role: e.target.value })}
                  style={{ width: '100%', padding: '10px', border: '1px solid #E0E0E0', borderRadius: '8px', fontSize: '14px' }}
                >
                  <option value="user">Uživatel</option>
                  <option value="manager">Manažer</option>
                </select>
              </div>
              <div style={{ display: 'flex', gap: '12px' }}>
                <button
                  type="button"
                  onClick={() => setShowUserModal(false)}
                  style={{
                    flex: 1,
                    padding: '12px',
                    border: '1px solid #E0E0E0',
                    borderRadius: '8px',
                    backgroundColor: '#FFF',
                    cursor: 'pointer',
                    fontSize: '14px'
                  }}
                >
                  Zrušit
                </button>
                <button
                  type="submit"
                  style={{
                    flex: 1,
                    padding: '12px',
                    backgroundColor: '#E53935',
                    color: '#FFF',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: 500
                  }}
                >
                  Uložit
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <ConfirmModal
        show={!!confirmDelete}
        title="Smazat uživatele?"
        message={`Opravdu chcete smazat uživatele ${confirmDelete?.username}? Tato akce je nevratná.`}
        confirmText="Smazat"
        cancelText="Zrušit"
        danger={true}
        onConfirm={() => handleDeleteUser(confirmDelete)}
        onCancel={() => setConfirmDelete(null)}
      />
    </div>
  );
};

export default AdminPanel;